/**
 * Products form hook: list/create/edit/delete products.
 * @typedef {import('../../../models/Product').Product} Product
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { getProducts } from '../../../services/productService';
import { getProductCategories } from '../../../services/productCategoryService';
import { getMlbCategoryAttributes } from '../../../services/mlbCategoriesService';
import { getProductAdditionalDetails } from '../../../services/productAdditionalDetailService';
import { getProductPictures, getPicturePublicUrl } from '../../../services/productPicturesService';
import { getProductSuppliers } from '../../../services/productSuppliersService';
import { getSuppliers } from '../../../services/supplierService';
import { useClickOutside, TrashIcon, distinctByKey, createHandleChange, clearFormAlerts } from '../../common';
import { SUPPLIERS_QUERY_KEY } from '../../common/utils/queryKeys';
import {
  PRODUCTS_QUERY_KEY,
  PRODUCT_CATEGORIES_QUERY_KEY,
  LIST_STALE_TIME_MS,
  emptyFormData,
  productToFormData,
  PICTURE_ACCEPT,
  PICTURE_MAX_SIZE_BYTES,
  additionalDetailRowsFromMlbAttributes,
  mergeAdditionalDetailsWithMlbAttrs,
  enrichAdditionalDetailRowsWithMlbNames
} from './productFormUtils';
import { createProductsFormSetters } from './productsFormSetters';
import { useProductFormSubmit } from './useProductFormSubmit';
import { useAppStore, getEmptyProductSupplierRow, normalizeProductSupplierRow } from '../../../store/appStore';

export { TrashIcon };

export function useProductsForm() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const productsForm = useAppStore((s) => s.productsForm);
  const setProductsForm = useAppStore((s) => s.setProductsForm);
  const setInsertBasicData = useAppStore((s) => s.setInsertBasicData);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedProduct,
    productSearchQuery,
    showProductDropdown,
    productNameFieldTouched,
    categoryFieldTouched,
    categorySearchQuery,
    subCategorySearchQuery,
    showCategoryDropdown,
    showSubCategoryDropdown,
    additionalDetails,
    pendingAdditionalDetailDeletes,
    productSuppliers,
    pendingProductSupplierDeletes,
    pendingPictureAdds,
    pendingPictureDeletes,
    pictureFileError,
    openDetailNameDropdownIndex,
    openSupplierDropdownIndex,
    attributesFromCategoryLoading,
    productSuppliersSyncedForProductId
  } = productsForm;

  const setters = React.useMemo(
    () => createProductsFormSetters(setProductsForm),
    [setProductsForm]
  );
  const {
    setMode,
    setFormData,
    setFormError,
    setFormSuccess,
    setSubmitting,
    setDeleting,
    setSelectedProduct,
    setProductSearchQuery,
    setShowProductDropdown,
    setProductNameFieldTouched,
    setCategoryFieldTouched,
    setCategorySearchQuery,
    setSubCategorySearchQuery,
    setShowCategoryDropdown,
    setShowSubCategoryDropdown,
    setAdditionalDetails,
    setPendingAdditionalDetailDeletes,
    setProductSuppliers,
    setPendingProductSupplierDeletes,
    setPendingPictureAdds,
    setPendingPictureDeletes,
    setPictureFileError,
    setOpenDetailNameDropdownIndex,
    setOpenSupplierDropdownIndex,
    setAttributesFromCategoryLoading
  } = setters;

  const { handleSubmit, handleDelete } = useProductFormSubmit(queryClient, token, setters);

  const productDropdownRef = React.useRef(null);
  const categoryDropdownRef = React.useRef(null);
  const subCategoryDropdownRef = React.useRef(null);
  const detailNameDropdownRef = React.useRef(null);
  const supplierDropdownRef = React.useRef(null);
  const pendingPictureAddsRef = React.useRef([]);

  const { data: productsResponse, isLoading: productsLoading } = useQuery({
    queryKey: PRODUCTS_QUERY_KEY,
    queryFn: () => getProducts(token),
    enabled: !!token && mode === 'edit' && productNameFieldTouched,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery({
    queryKey: PRODUCT_CATEGORIES_QUERY_KEY,
    queryFn: () => getProductCategories(token),
    enabled: !!token && (categoryFieldTouched || (mode === 'edit' && !!selectedProduct?.id)),
    staleTime: LIST_STALE_TIME_MS
  });

  const {
    data: additionalDetailsResponse,
    isLoading: additionalDetailsLoading,
    isFetching: additionalDetailsFetching
  } = useQuery({
    queryKey: ['product-additional-details', selectedProduct?.id],
    queryFn: () => getProductAdditionalDetails(token, selectedProduct.id),
    enabled: !!token && !!selectedProduct?.id
  });

  const {
    data: productPicturesResponse,
    isLoading: productPicturesLoading,
    isFetching: productPicturesFetching
  } = useQuery({
    queryKey: ['product-pictures', selectedProduct?.id],
    queryFn: () => getProductPictures(token, selectedProduct.id),
    enabled: !!token && !!selectedProduct?.id
  });

  const {
    data: productSuppliersResponse,
    isLoading: productSuppliersLoading,
    isFetching: productSuppliersFetching
  } = useQuery({
    queryKey: ['product-suppliers', selectedProduct?.id],
    queryFn: () => getProductSuppliers(token, { product_id: selectedProduct.id }),
    enabled: !!token && !!selectedProduct?.id && mode === 'edit'
  });

  const [additionalDetailsSyncedForProductId, setAdditionalDetailsSyncedForProductId] = React.useState(null);
  React.useEffect(() => {
    setAdditionalDetailsSyncedForProductId(null);
  }, [selectedProduct?.id]);

  const isEditMode = mode === 'edit';
  const additionalDetailsStillSyncing =
    isEditMode &&
    !!selectedProduct?.id &&
    additionalDetailsSyncedForProductId !== selectedProduct.id;

  const sectionLoading =
    productsLoading ||
    categoriesLoading ||
    (additionalDetailsLoading || additionalDetailsFetching || additionalDetailsStillSyncing) ||
    (productPicturesLoading || productPicturesFetching) ||
    (productSuppliersLoading || productSuppliersFetching);
  React.useEffect(() => {
    setInsertBasicData((prev) => ({
      ...prev,
      sectionLoading: { ...prev.sectionLoading, products: sectionLoading }
    }));
  }, [sectionLoading, setInsertBasicData]);

  const { data: suppliersResponse } = useQuery({
    queryKey: SUPPLIERS_QUERY_KEY,
    queryFn: () => getSuppliers(token),
    enabled: !!token,
    staleTime: LIST_STALE_TIME_MS
  });

  const products = productsResponse?.products ?? [];
  const categories = React.useMemo(
    () => categoriesResponse?.product_categories ?? [],
    [categoriesResponse?.product_categories]
  );
  const suppliers = suppliersResponse?.suppliers ?? [];

  const categoryRow = React.useMemo(() => {
    const cid = formData.category_id;
    const sid = formData.sub_category_id;
    if (cid == null || sid == null) return null;
    return categories.find((r) => r.id === cid && r.sub_category_id === sid) ?? null;
  }, [categories, formData.category_id, formData.sub_category_id]);

  const mlbCategoryId = categoryRow?.mlb_category_child_id || categoryRow?.mlb_category_id || null;

  /** In edit mode, MLB category id for the selected product's category; used to fetch attributes once via React Query. */
  const editMlbCategoryId = React.useMemo(() => {
    if (mode !== 'edit' || selectedProduct?.category_id == null || selectedProduct?.sub_category_id == null)
      return null;
    const row = categories.find(
      (r) => r.id === selectedProduct?.category_id && r.sub_category_id === selectedProduct?.sub_category_id
    );
    return row?.mlb_category_child_id || row?.mlb_category_id || null;
  }, [mode, selectedProduct?.category_id, selectedProduct?.sub_category_id, categories]);

  const { data: mlbCategoryAttributesResponse } = useQuery({
    queryKey: ['mlb-category-attributes', editMlbCategoryId],
    queryFn: () => getMlbCategoryAttributes(token, editMlbCategoryId),
    enabled: !!token && !!editMlbCategoryId && mode === 'edit'
  });

  const loadAttributesFromCategory = React.useCallback(async () => {
    if (!token || !mlbCategoryId) return;
    setAttributesFromCategoryLoading(true);
    try {
      const data = await getMlbCategoryAttributes(token, mlbCategoryId);
      const attrs = data?.attributes ?? [];
      if (attrs.length === 0) {
        setAttributesFromCategoryLoading(false);
        return;
      }
      if (mode === 'create') {
        setAdditionalDetails(additionalDetailRowsFromMlbAttributes(attrs));
      } else {
        setAdditionalDetails((prev) => mergeAdditionalDetailsWithMlbAttrs(prev, attrs));
      }
    } finally {
      setAttributesFromCategoryLoading(false);
    }
  }, [token, mlbCategoryId, mode]);

  React.useEffect(() => {
    if (mode !== 'edit' || additionalDetailsResponse?.product_additional_details == null) return;

    const productId = selectedProduct?.id;
    const fromApi = additionalDetailsResponse.product_additional_details.map((d) => ({
      ...d,
      attribute_id: d.attribute_id ?? d.mlb_attribute_id
    }));
    const rows = fromApi.filter((d) => !pendingAdditionalDetailDeletes.includes(d.id));
    const hasAnyMlbAttr = rows.some((d) => d.mlb_attribute_id || d.attribute_id);

    const markSynced = () => {
      if (productId != null) setAdditionalDetailsSyncedForProductId(productId);
    };

    if (!editMlbCategoryId || !hasAnyMlbAttr) {
      setAdditionalDetails(rows);
      markSynced();
      return;
    }

    const data = mlbCategoryAttributesResponse;
    if (data == null) return;

    const attrs = data?.attributes ?? [];
    const enriched = enrichAdditionalDetailRowsWithMlbNames(rows, attrs);
    setAdditionalDetails(enriched);
    markSynced();
  }, [
    mode,
    additionalDetailsResponse?.product_additional_details,
    pendingAdditionalDetailDeletes,
    selectedProduct?.id,
    editMlbCategoryId,
    mlbCategoryAttributesResponse
  ]);

  React.useEffect(() => {
    if (mode !== 'edit' || productSuppliersResponse?.product_suppliers == null || selectedProduct?.id == null) return;
    if (productSuppliersSyncedForProductId === selectedProduct.id) return;
    const fromApi = productSuppliersResponse.product_suppliers;
    const first = fromApi[0];
    if (fromApi.length > 0 && first?.product_id != null && first.product_id !== selectedProduct.id) return;
    const rows = fromApi
      .filter((d) => !pendingProductSupplierDeletes.includes(d.id))
      .map(normalizeProductSupplierRow);
    setProductSuppliers(rows);
    setProductsForm((prev) => ({ ...prev, productSuppliersSyncedForProductId: selectedProduct.id }));
  }, [
    mode,
    selectedProduct?.id,
    productSuppliersResponse?.product_suppliers,
    pendingProductSupplierDeletes,
    productSuppliersSyncedForProductId,
    setProductSuppliers,
    setProductsForm
  ]);

  React.useEffect(() => {
    if (mode === 'edit' && selectedProduct?.id) {
      setPendingPictureDeletes([]);
      setPendingAdditionalDetailDeletes([]);
      setPendingProductSupplierDeletes([]);
    }
  }, [mode, selectedProduct?.id, setPendingPictureDeletes, setPendingAdditionalDetailDeletes, setPendingProductSupplierDeletes]);

  const filteredProducts = React.useMemo(() => {
    if (!productSearchQuery.trim()) return products;
    const q = productSearchQuery.trim().toLowerCase();
    return products.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [products, productSearchQuery]);

  const distinctCategories = React.useMemo(() => distinctByKey(categories, 'id'), [categories]);

  const filteredCategoryOptions = React.useMemo(() => {
    if (!categorySearchQuery.trim()) return distinctCategories;
    const q = categorySearchQuery.trim().toLowerCase();
    return distinctCategories.filter((row) =>
      (row.category_name || '').toLowerCase().includes(q)
    );
  }, [distinctCategories, categorySearchQuery]);

  const subCategoryOptions = React.useMemo(
    () => categories.filter((row) => row.id === formData.category_id),
    [categories, formData.category_id]
  );

  const filteredSubCategoryOptions = React.useMemo(() => {
    if (!subCategorySearchQuery.trim()) return subCategoryOptions;
    const q = subCategorySearchQuery.trim().toLowerCase();
    return subCategoryOptions.filter((row) =>
      (row.sub_category_name || '').toLowerCase().includes(q)
    );
  }, [subCategoryOptions, subCategorySearchQuery]);

  React.useEffect(() => {
    if (categories.length === 0) return;
    const cid = formData.category_id;
    const sid = formData.sub_category_id;
    if (cid == null && sid == null) return;
    const row = categories.find((r) => r.id === cid && r.sub_category_id === sid);
    if (row && (formData.categoryDisplayName !== (row.category_name || '') || formData.subCategoryDisplayName !== (row.sub_category_name || ''))) {
      setFormData((prev) => ({
        ...prev,
        categoryDisplayName: row.category_name || '',
        subCategoryDisplayName: row.sub_category_name || ''
      }));
    }
  }, [categories, formData.category_id, formData.sub_category_id]);

  useClickOutside(
    [productDropdownRef, categoryDropdownRef, subCategoryDropdownRef, detailNameDropdownRef, supplierDropdownRef],
    () => {
      setShowProductDropdown(false);
      setShowCategoryDropdown(false);
      setShowSubCategoryDropdown(false);
      setOpenSupplierDropdownIndex(null);
      setOpenDetailNameDropdownIndex(null);
    }
  );

  const handleChange = React.useMemo(
    () => createHandleChange(setFormData, setFormError, setFormSuccess),
    [setFormData, setFormError, setFormSuccess]
  );

  const handleModeChange = (newMode) => {
    setMode(newMode);
    clearFormAlerts(setFormError, setFormSuccess);
    setFormData(emptyFormData());
    setSelectedProduct(null);
    setProductSearchQuery('');
    setShowProductDropdown(false);
    setCategorySearchQuery('');
    setSubCategorySearchQuery('');
    setShowCategoryDropdown(false);
    setShowSubCategoryDropdown(false);
    setAdditionalDetails([]);
    setPendingPictureAdds([]);
    setPendingPictureDeletes([]);
    setProductSuppliers([]);
    setPendingProductSupplierDeletes([]);
    setPictureFileError('');
    setOpenSupplierDropdownIndex(null);
    if (newMode === 'create') {
      setProductNameFieldTouched(false);
      setCategoryFieldTouched(false);
    }
  };

  const handleProductSearchChange = (e) => {
    setProductSearchQuery(e.target.value);
    setSelectedProduct(null);
    setShowProductDropdown(true);
  };

  /** @param {Product} product */
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setFormData(productToFormData(product));
    setProductSearchQuery(product.name || '');
    setShowProductDropdown(false);
    setAdditionalDetails([]);
    setPendingPictureAdds([]);
    setPendingPictureDeletes([]);
    setProductSuppliers([]);
    setPendingProductSupplierDeletes([]);
    setPictureFileError('');
    setOpenSupplierDropdownIndex(null);
    clearFormAlerts(setFormError, setFormSuccess);
  };

  const handleCategoryFocus = () => {
    setCategoryFieldTouched(true);
    setShowCategoryDropdown(true);
  };

  const handleSelectCategory = (row) => {
    setFormData((prev) => ({
      ...prev,
      category_id: row.id,
      sub_category_id: null,
      categoryDisplayName: row.category_name || '',
      subCategoryDisplayName: ''
    }));
    setCategorySearchQuery(row.category_name || '');
    setSubCategorySearchQuery('');
    setShowCategoryDropdown(false);
    setShowSubCategoryDropdown(false);
  };

  const handleSubCategoryFocus = () => {
    setCategoryFieldTouched(true);
    if (formData.category_id != null) setShowSubCategoryDropdown(true);
  };

  const handleSelectSubCategory = (row) => {
    setFormData((prev) => ({
      ...prev,
      sub_category_id: row.sub_category_id,
      subCategoryDisplayName: row.sub_category_name || ''
    }));
    setSubCategorySearchQuery(row.sub_category_name || '');
    setShowSubCategoryDropdown(false);
  };

  const addAdditionalDetail = () => {
    setAdditionalDetails((prev) => [
      ...prev,
      { detail_group: '', detail_name: '', detail_value: '', attribute_id: null, attribute_name: '', allowed_values: [] }
    ]);
  };

  const updateAdditionalDetail = (index, field, value) => {
    setAdditionalDetails((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const removeAdditionalDetail = (index) => {
    const item = additionalDetails[index];
    if (item?.id != null) {
      setPendingAdditionalDetailDeletes((ids) => [...ids, item.id]);
    }
    setAdditionalDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const addProductSupplierLink = () => {
    setProductSuppliers((prev) => [...prev, getEmptyProductSupplierRow()]);
  };

  const updateProductSupplierLink = (index, field, value) => {
    setProductSuppliers((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeProductSupplierLink = (index) => {
    const item = productSuppliers[index];
    if (item?.id != null) {
      setPendingProductSupplierDeletes((ids) => [...ids, item.id]);
    }
    setProductSuppliers((prev) => prev.filter((_, i) => i !== index));
  };

  const existingPictures = (productPicturesResponse?.product_pictures ?? []).filter(
    (p) => !pendingPictureDeletes.includes(p.id)
  );

  const pictureDisplayItems = React.useMemo(() => {
    const items = [];
    existingPictures.forEach((p) => {
      const url = (p.picture_path && getPicturePublicUrl(p.picture_path)) || p.picture_url || '';
      items.push({ type: 'existing', id: p.id, url });
    });
    pendingPictureAdds.forEach((item, index) => {
      items.push({ type: 'pending', pendingIndex: index, url: item.preview, file: item.file });
    });
    return items;
  }, [existingPictures, pendingPictureAdds]);

  const addPictures = (files) => {
    setPictureFileError('');
    if (!files?.length) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
    const filesToAdd = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!allowed.includes(file.type)) {
        setPictureFileError(`Allowed formats: JPG, PNG. "${file.name}" is not supported.`);
        return;
      }
      if (file.size > PICTURE_MAX_SIZE_BYTES) {
        setPictureFileError(`Max size 10 MB. "${file.name}" is too large.`);
        return;
      }
      filesToAdd.push(file);
    }
    const readPreviews = filesToAdd.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ file, preview: reader.result });
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(readPreviews).then((next) => {
      setPendingPictureAdds((prev) => {
        const nextState = [...prev, ...next];
        pendingPictureAddsRef.current = nextState;
        return nextState;
      });
    }).catch(() => {
      setPictureFileError('Could not read image for preview.');
    });
  };

  const removePicture = (item) => {
    if (item.type === 'existing') {
      setPendingPictureDeletes((prev) => [...prev, item.id]);
    } else {
      setPendingPictureAdds((prev) => {
        const next = prev.filter((_, i) => i !== item.pendingIndex);
        pendingPictureAddsRef.current = next;
        return next;
      });
    }
  };

  React.useEffect(() => {
    pendingPictureAddsRef.current = pendingPictureAdds;
  }, [pendingPictureAdds]);

  return {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedProduct,
    productSearchQuery,
    showProductDropdown,
    productNameFieldTouched,
    productsLoading,
    categorySearchQuery,
    subCategorySearchQuery,
    showCategoryDropdown,
    showSubCategoryDropdown,
    categoriesLoading,
    filteredProducts,
    filteredCategoryOptions,
    filteredSubCategoryOptions,
    productDropdownRef,
    categoryDropdownRef,
    subCategoryDropdownRef,
    detailNameDropdownRef,
    openDetailNameDropdownIndex,
    setOpenDetailNameDropdownIndex,
    handleModeChange,
    handleChange,
    handleProductSearchChange,
    handleSelectProduct,
    handleCategoryFocus,
    handleSelectCategory,
    handleSubCategoryFocus,
    handleSelectSubCategory,
    handleSubmit,
    handleDelete,
    isEditMode,
    setCategorySearchQuery,
    setSubCategorySearchQuery,
    setFormData,
    setProductNameFieldTouched,
    setShowProductDropdown,
    setShowCategoryDropdown,
    setShowSubCategoryDropdown,
    additionalDetails,
    additionalDetailsLoading,
    addAdditionalDetail,
    updateAdditionalDetail,
    removeAdditionalDetail,
    loadAttributesFromCategory,
    attributesFromCategoryLoading,
    hasCategoryForAttributes: !!mlbCategoryId,
    pictureDisplayItems,
    productPicturesLoading,
    pictureFileError,
    addPictures,
    removePicture,
    productSuppliers,
    productSuppliersLoading,
    suppliers,
    addProductSupplierLink,
    updateProductSupplierLink,
    removeProductSupplierLink,
    supplierDropdownRef,
    openSupplierDropdownIndex,
    setOpenSupplierDropdownIndex
  };
}
